// Bootstrap EE — biz-bs-ee profile entry point
// Full bundle: Bootstrap + DataTables + jQuery + all BSEE modules + utility libs.

import jQuery from 'jquery';
// Expose jQuery globally so jQuery plugins bundled below can attach to it.
(window as any).jQuery = jQuery;
(window as any).$ = jQuery;

import DataTable from 'datatables.net-bs5';
import 'datatables.net-buttons-bs5';
import ColumnControl from 'datatables.net-columncontrol-bs5';
import 'datatables.net-responsive-bs5';
import 'datatables.net-select-bs5';
import 'datatables.net-searchbuilder-bs5';
import 'datatables.net-rowgroup-bs5';
import 'datatables.net-rowreorder-bs5';
import 'datatables.net-fixedheader-bs5';
import 'datatables.net-fixedcolumns-bs5';
import 'datatables.net-colreorder-bs5';
import 'datatables.net-keytable-bs5';
import 'datatables.net-scroller-bs5';
import 'datatables.net-autofill-bs5';

// DataTables 3 is jQuery-free and only attaches $.fn.DataTable when window.jQuery exists at its
// own module evaluation — which runs BEFORE the window assignment above (imports are hoisted).
// Registering explicitly keeps every legacy `$('…').DataTable()` page working.
DataTable.use(jQuery);

import {
  Alert,
  Button,
  Carousel,
  Collapse,
  Dropdown,
  Modal,
  Offcanvas,
  Popover,
  ScrollSpy,
  Tab,
  Toast,
  Tooltip,
} from 'bootstrap';

import './tablesorter';
import './typeahead';
import './ace';
import './doc-tags';
import './doc-editor';

import { animate } from './bsee-animate';
import { dropdownHover } from './bsee-dropdown-hover';
import { onInsert } from './bsee-on-insert';
import { toast } from './toast';
import { guide } from './bsee-guide';
import { dayjs } from './dayjs';
import { ClipboardJS, initBseeCopy } from './bsee-copy';
import { initBseeEditor } from './bsee-editor';
import { initBseeMdChecklist } from './bsee-md-checklist';
import { Sortable } from './sortable';
import { bizTable } from './biz-table';
import { flatpickr } from './flatpickr';
import { Coloris } from './coloris';
import { TomSelect, initTomSelects } from './tomselect';
import { Bloodhound } from './typeahead';
import { filetype } from './bsee-filetype';
import { datatable } from './bsee-datatable';
import { debounce } from './debounce';
import { bseeValidation } from './bsee-validation';
import { bseeCsrf } from './bsee-csrf';
import { uiState } from './bsee-ui-state';
import { Sherlock } from './sherlock';
import { startBugsnag, bugsnagDropNoise, Bugsnag } from './bsee-bugsnag';

// First, before anything else can throw: report errors from this page (if it carries the
// bugsnag-api-key meta tag). Called explicitly — a lib build drops side-effect-only module bodies.
startBugsnag();

// Expose Sherlock globally — ca/page/_todo_form_panel.php calls the bare global
// `Sherlock.parse(...)` for natural-language return-date entry.
(window as any).Sherlock = Sherlock;

export { animate };
export { dropdownHover };
export type { DropdownHoverOptions } from './bsee-dropdown-hover';
export { onInsert };
export { toast };
export type { ToastOptions } from './toast';
export { guide };
export type { GuideAdvice, GuideChoice, GuideChoices, GuideOptions } from './bsee-guide';
export { dayjs };
export { ClipboardJS, initBseeCopy };
export { initBseeEditor };
export { initBseeMdChecklist };
export { Sortable };
export { flatpickr };
export { Coloris };
export { TomSelect, initTomSelects };
export { Bloodhound };
export { filetype };
export { datatable };
export { debounce };
export { bizTable };
export type { LinkOptions, ButtonOptions, BadgeOptions, CheckmarkOptions, DateOptions, TextOptions, NumberOptions, HeatOptions } from './biz-table';
export { bseeValidation };
export { bseeCsrf };
export { uiState };
export { Sherlock };
export { startBugsnag, bugsnagDropNoise, Bugsnag };

// Expose Bootstrap components as window.bootstrap so data-bs-* data-API works.
(window as any).bootstrap = {
    Alert, Button, Carousel, Collapse, Dropdown,
    Modal, Offcanvas, Popover, ScrollSpy, Tab, Toast, Tooltip,
};

// Auto-init on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
    dropdownHover.init();

    // Called here rather than left to the module's own DOM-ready hook: this
    // bundle is a lib build, so Rollup drops a module's side-effect body when
    // the entry only imports its names. An explicit call is what keeps it.
    initTomSelects();

    document.querySelectorAll('[data-bs-toggle="collapse"]').forEach(el => {
        Collapse.getOrCreateInstance(el as HTMLElement, { toggle: false });
    });

    document.querySelectorAll('[data-bs-toggle="tooltip"]').forEach(el => {
        Tooltip.getOrCreateInstance(el as HTMLElement);
    });

    document.querySelectorAll('[data-bs-toggle="popover"]').forEach(el => {
        Popover.getOrCreateInstance(el as HTMLElement);
    });

    filetype.scan();
    // NOTICE 2026-05-27 BSEE auto-init disabled — BIZ owns #grid init via ca/report/datatable_setup.js
    // (and per-page custom DataTable() calls). Re-enabling would double-init and silently override
    // page-level options. datatable.initGrid is still exported on window.bsee for explicit opt-in.
    // datatable.initGrid();

    // Opt-in JSON-fed tables (<table data-bsee-table data-bsee-url="…">). Never matches #grid
    // reports, which carry no data-bsee-table, so it cannot double-init them.
    datatable.scan();

    // Auto-init tooltips and popovers on dynamically injected content
    onInsert('[data-bsee-select]', (el) => {
        initTomSelects(el.parentElement ?? document);
    });

    onInsert('[data-bs-toggle="tooltip"]', (el) => {
        Tooltip.getOrCreateInstance(el as HTMLElement);
    });

    onInsert('[data-bs-toggle="popover"]', (el) => {
        Popover.getOrCreateInstance(el as HTMLElement);
    });
});

export {
  DataTable,
  ColumnControl,
  Alert,
  Button,
  Carousel,
  Collapse,
  Dropdown,
  Modal,
  Offcanvas,
  Popover,
  ScrollSpy,
  Tab,
  Toast,
  Tooltip,
};
